import React from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import useDelayUnmount from '../utils/hooks/useDelayUnmount';
import { StyledBackdrop, StyledDrawer, StyledDrawerContainer } from './styles';

export interface DrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean;
  onClose?: () => void;
  position?: 'top' | 'bottom' | 'left' | 'right';
  variant?: 'temporary' | 'permanent';
}

const Drawer: React.FC<DrawerProps> = ({
  open = false,
  position = 'bottom',
  children,
  onClose,
  variant = 'temporary',
  ...props
}) => {
  const prevVariant = useRef<'temporary' | 'permanent'>(variant);
  useEffect(() => {
    prevVariant.current = variant;
  }, [variant]);

  const shouldRender = useDelayUnmount(
    open,
    prevVariant.current === 'permanent' ? 0 : 255
  );

  // Hide body scrollbar when open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [open]);

  // Close when esc key is pressed
  useEffect(() => {
    if (!open || !onClose) return;

    const handleEscPressed = function (event: KeyboardEvent) {
      let isEscape = false;
      if (event.key !== '') {
        isEscape = event.key === 'Escape' || event.key === 'Esc';
      } else {
        isEscape = event['keyCode'] === 27;
      }
      if (isEscape) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscPressed, true);
    return () => {
      window.removeEventListener('keydown', handleEscPressed);
    };
  }, [open, onClose]);

  if (!shouldRender) return null;
  if (variant === 'permanent') {
    return (
      <StyledDrawerContainer
        position={position}
        show
        variant="permanent"
        {...props}
      >
        {children}
      </StyledDrawerContainer>
    );
  }

  return createPortal(
    <StyledDrawer role="presentation">
      <StyledBackdrop
        show={open}
        onClick={() => onClose && onClose()}
        aria-hidden
        data-testid="drawer-backdrop"
      />
      <StyledDrawerContainer
        position={position}
        show={open}
        variant="temporary"
        {...props}
      >
        {children}
      </StyledDrawerContainer>
    </StyledDrawer>,
    document.body
  );
};

export default Drawer;
